import React, { useState, useEffect } from 'react';
import { X, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';

export default function AgentModal({ isOpen, onClose, onExecute, onOpenCreditModal }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [query, setQuery] = useState('');
  const [executing, setExecuting] = useState(false);
  const [creditStatus, setCreditStatus] = useState(null);
  const [showInsufficientCreditModal, setShowInsufficientCreditModal] = useState(false);
  const [insufficientCreditAgent, setInsufficientCreditAgent] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
      fetchCreditStatus();
    }
  }, [isOpen]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/agents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAgents(response.data.agents);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/credits/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch credit status:', error);
    }
  };

  const handleExecute = async () => {
    if (!selectedAgent || !query.trim()) return;

    try {
      setExecuting(true);
      
      // Show loading animation immediately for deep research
      if (selectedAgent.id === 'deep_research') {
        onExecute({
          showLoading: true,
          agent_name: selectedAgent.name
        });
      }
      
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        '/api/agents/execute',
        {
          agent_id: selectedAgent.id,
          query: query.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Pass result to parent
        onExecute(response.data);
        // Reset and close
        setQuery('');
        setSelectedAgent(null);
        onClose();
      } else if (response.data.error === 'insufficient_credits') {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Agent execution failed:', error);
      
      // Retry logic for server errors
      if (error.response?.status === 500 && !error.config?.retryCount) {
        console.log('Server error, retrying in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        error.config = error.config || {};
        error.config.retryCount = 1;
        return handleExecute();
      }
      
      alert('Agent çalıştırılamadı. Lütfen tekrar deneyin.');
    } finally {
      setExecuting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Zeha Agents
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Agent Selection */}
              <div className="space-y-3 mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agent Seç
                </h3>
                {agents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => {
                      // Kredi kontrolü yap
                      if (creditStatus && creditStatus.total_available < agent.credit_cost) {
                        setInsufficientCreditAgent(agent);
                        setShowInsufficientCreditModal(true);
                        return;
                      }
                      setSelectedAgent(agent);
                    }}
                    className={`
                      w-full p-4 rounded-lg border-2 text-left transition-all
                      ${selectedAgent?.id === agent.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{agent.icon}</span>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {agent.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {agent.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {agent.credit_cost} kredi
                        </span>
                        {creditStatus && creditStatus.total_available < agent.credit_cost && (
                          <span className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Yetersiz kredi
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Query Input */}
              {selectedAgent && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Sorunuz
                  </h3>
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={`${selectedAgent.name} için sorunuzu yazın...`}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                    rows={4}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {selectedAgent && (
          <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleExecute}
              disabled={!query.trim() || executing}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {executing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Çalıştırılıyor...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Çalıştır ({selectedAgent.credit_cost} kredi)</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Insufficient Credit Modal */}
      {showInsufficientCreditModal && insufficientCreditAgent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-sm w-full shadow-2xl border border-gray-200 dark:border-gray-800">
            {/* Header */}
            <div className="p-5 pb-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Yetersiz Kredi
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    Bu özellik için daha fazla kredi gerekiyor
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 pb-5">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Özellik</span>
                  <span className="font-medium text-gray-900 dark:text-white">{insufficientCreditAgent.name}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Gerekli</span>
                  <span className="font-semibold text-orange-600 dark:text-orange-400">{insufficientCreditAgent.credit_cost} kredi</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Mevcut</span>
                  <span className="font-medium text-gray-900 dark:text-white">{creditStatus?.total_available || 0} kredi</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowInsufficientCreditModal(false);
                    setInsufficientCreditAgent(null);
                  }}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Kapat
                </button>
                <button
                  onClick={() => {
                    setShowInsufficientCreditModal(false);
                    setInsufficientCreditAgent(null);
                    onClose();
                    if (onOpenCreditModal) {
                      onOpenCreditModal();
                    }
                  }}
                  className="flex-1 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-all"
                >
                  Kredi Satın Al
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
